import { useState, useEffect } from "react";
import Button from "~/components/ui/Button";

/**
 * Cookie consent inline notice.
 * Positioned under the footer at the bottom of the page.
 * Stores consent with timestamp in localStorage and integrates with Google Analytics.
 * Consent expires after 1 year.
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consentData = localStorage.getItem("cookie-consent");

    if (!consentData) {
      // No consent stored
      setVisible(true);
      return;
    }

    try {
      const { accepted, timestamp } = JSON.parse(consentData);

      if (!accepted || !timestamp) {
        // Invalid format - show consent again
        setVisible(true);
        return;
      }

      // Check if consent is less than 1 year old
      const oneYearInMs = 365 * 24 * 60 * 60 * 1000;
      const consentAge = Date.now() - timestamp;

      if (consentAge > oneYearInMs) {
        // Consent expired - show consent again
        localStorage.removeItem("cookie-consent");
        setVisible(true);
        return;
      }

      // Valid consent within 1 year - enable analytics
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'granted'
        });

        // Send initial page_view for returning users
        window.gtag('event', 'page_view', {
          page_location: window.location.href,
          page_path: window.location.pathname,
          page_title: document.title
        });
      }
    } catch (error) {
      // Invalid JSON - show consent again
      console.error('Failed to parse cookie consent:', error);
      localStorage.removeItem("cookie-consent");
      setVisible(true);
    }
  }, []);

  const acceptCookies = (): void => {
    const consentData = {
      accepted: true,
      timestamp: Date.now()
    };
    localStorage.setItem("cookie-consent", JSON.stringify(consentData));
    setVisible(false);

    // Grant analytics consent and send the current page as first page_view
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });

      // Send the current page as the first page_view event
      window.gtag('event', 'page_view', {
        page_location: window.location.href,
        page_path: window.location.pathname,
        page_title: document.title
      });
    }
  };

  if (!visible) return null;

  return (
    <div className="border-t-2 border-gray-800 bg-gray-950">
      <div className="px-4 py-3 flex flex-row gap-3 items-center justify-end">
        <p className="text-sm text-gray-300">
          We use essential cookies for security and optional cookies for analytics.
        </p>
        <Button
          onClick={acceptCookies}
          variant="secondary"
          size="sm"
          className="border-gray-700 bg-transparent hover:border-green-500 text-gray-200 hover:text-white focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-950 whitespace-nowrap"
        >
          Accept
        </Button>
      </div>
    </div>
  );
}